import { useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { importManualFlightsCsv } from "../../api/ManualFlightsApi";
import { toast } from "sonner";

// если у тебя уже есть функция импорта:

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported?: () => void; // чтобы после импорта обновить таблицу
};

interface UploadError {
  line: number;
  code: string;
  message: string;
}

const formatBytes = (bytes: number) => {
  if (!Number.isFinite(bytes)) return "";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)}MB`;
  const kb = bytes / 1024;
  return `${Math.max(1, Math.round(kb))}KB`;
};

export function ManualFlightsModal({ open, onOpenChange, onImported }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileSize = useMemo(() => (file ? formatBytes(file.size) : ""), [file]);

  const openPicker = () => inputRef.current?.click();

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    e.target.value = ""; // важно: чтобы можно было выбрать тот же файл повторно

    setError(null);

    if (!f) return;

    const isCsv =
      f.type === "text/csv" || f.name.toLowerCase().endsWith(".csv");

    if (!isCsv) {
      setError("Пожалуйста, выберите CSV файл (.csv).");
      setFile(null);
      return;
    }

    setFile(f);
  };

  const removeFile = () => {
    setFile(null);
    setError(null);
  };

  const handleImport = async () => {
    if (!file) {
      setError("Сначала выберите CSV файл.");
      return;
    }

    try {
      setIsImporting(true);
      setError(null);

      const response = await importManualFlightsCsv(file);

      if (response.errors) {
        toast.error(
          <div>
            {response.errors.map((e: UploadError, i: number) => (
              <div key={i}>
                Строка {e.line}: {e.message}
              </div>
            ))}
          </div>
        );
        return;
      }

      onImported?.();
      onOpenChange(false);
      toast.success(`Количество добавленных рейсов: ${response.imported}`);
      // можно очистить после закрытия
      setFile(null);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Не удалось добавить пользователя";
      toast.error(message);
      setError(
        "Не удалось импортировать CSV. Проверьте файл и попробуйте снова."
      );
    } finally {
      setIsImporting(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (!next) {
      // при закрытии — сброс
      setFile(null);
      setError(null);
      setIsImporting(false);
    }
  };

  const truncateFileName = (name: string, maxLength = 30): string => {
    if (name.length <= maxLength) return name;
    const ext = name.slice(name.lastIndexOf(".")); // ".csv"
    const base = name.slice(0, maxLength - ext.length - 3); // оставляем место для "..."
    return `${base}...${ext}`;
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[560px] w-[560px] p-6">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-xl font-semibold">
            Загрузка таблицы
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            Автоматически пересчитывается таблица
          </DialogDescription>
        </DialogHeader>

        {/* hidden file input */}
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={onPickFile}
        />

        {/* File block */}
        <div className="mt-5">
          {!file ? (
            <button
              type="button"
              onClick={openPicker}
              className="w-full rounded-lg border border-dashed bg-white px-4 py-5 text-left hover:bg-gray-50 transition"
            >
              <div className="text-sm font-medium">Выберите CSV файл</div>
              <div className="text-xs text-gray-500 mt-1">
                Нажмите, чтобы загрузить
              </div>
            </button>
          ) : (
            <div className="rounded-lg border bg-white px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {/* иконка слева (простая) */}
                <div className="h-10 w-10 rounded-md bg-emerald-50 flex items-center justify-center text-emerald-600 font-semibold">
                  CSV
                </div>

                <div className="min-w-0 flex-1">
                  <div className="font-semibold" title={file.name}>
                    {truncateFileName(file.name)}
                  </div>
                  <div className="text-sm text-gray-500">{fileSize}</div>
                </div>
              </div>

              <button
                type="button"
                onClick={removeFile}
                className="h-9 w-9 rounded-full border bg-white text-gray-500 hover:bg-gray-50 transition flex items-center justify-center"
                aria-label="Удалить файл"
                title="Удалить файл"
              >
                ×
              </button>
            </div>
          )}

          {error ? (
            <div className="mt-2 text-sm text-red-600">{error}</div>
          ) : null}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={openPicker}
            className="flex-1 h-10"
            disabled={isImporting}
          >
            Загрузить еще
          </Button>

          <Button
            type="button"
            onClick={handleImport}
            className="flex-1 h-10"
            disabled={!file || isImporting}
          >
            {isImporting ? "Импорт..." : "Добавить"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
